import { useState, type KeyboardEvent } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useBrandThemeColors } from "../../../branding";
import {
  capAssessmentPrompt,
  getInlineSuggestionSuffix,
  isNonEmptySuggestionPrefixMatch,
  isSuggestionPrefixMatch,
  resolveTabCompletedPrompt,
} from "./buildAssessmentPrompt";

const TAB_HINT_COLOR = "#6A7282";
const GHOST_PLACEHOLDER_COLOR = "rgba(0, 0, 0, 0.38)";

export const ASSESSMENT_PROMPT_DEFAULT_INFO =
  "If no assessment prompt is provided, the system will apply the default prompt displayed in the grey placeholder. The default prompt is managed by the system and may be updated periodically.";

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

const sharedTextAreaTypographySx = {
  fontSize: "0.875rem",
  lineHeight: 1.5,
  fontFamily: "inherit",
  letterSpacing: "inherit",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
};

const outlinedInputSx = {
  borderRadius: "6px",
  width: "100%",
  alignItems: "flex-start",
  bgcolor: "transparent",
  fontSize: "0.875rem",
  lineHeight: 1.5,
  "& .MuiOutlinedInput-input": {
    ...sharedTextAreaTypographySx,
    position: "relative",
    zIndex: 1,
    bgcolor: "transparent",
  },
};

export const ASSESSMENT_PROMPT_MAX_LENGTH = 5000;

type AssessmentPromptFieldProps = Readonly<{
  value: string;
  suggestedPrompt: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  maxLength?: number;
  disabled?: boolean;
  required?: boolean;
  /** Show info icon next to the label (Add flow default-prompt hint). */
  showDefaultPromptInfo?: boolean;
  labelSx?: Record<string, unknown>;
  counterSx?: Record<string, unknown>;
  inputSx?: Record<string, unknown>;
}>;

export default function AssessmentPromptField({
  value,
  suggestedPrompt,
  onChange,
  error = false,
  helperText,
  maxLength = ASSESSMENT_PROMPT_MAX_LENGTH,
  disabled = false,
  required = false,
  showDefaultPromptInfo = false,
  labelSx,
  counterSx,
  inputSx,
}: AssessmentPromptFieldProps) {
  const { primaryColor } = useBrandThemeColors();
  const [focused, setFocused] = useState(false);
  const cappedSuggestion = capAssessmentPrompt(
    suggestedPrompt.trim(),
    maxLength,
  );

  // Empty, or non-empty prefix of suggestion (not fully equal).
  const showUseSuggested =
    !disabled && isSuggestionPrefixMatch(value, cappedSuggestion);
  // Focused + non-empty prefix match; hide when unfocused, mismatched, or fully equal.
  const showTabHint =
    !disabled &&
    focused &&
    isNonEmptySuggestionPrefixMatch(value, cappedSuggestion);
  const ghostSuffix =
    !disabled && showTabHint
      ? getInlineSuggestionSuffix(value, cappedSuggestion)
      : null;

  const applySuggestion = () => {
    const completed = resolveTabCompletedPrompt(
      value,
      cappedSuggestion,
      maxLength,
    );
    if (!completed) return;
    onChange(completed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Tab" || event.shiftKey || disabled) return;

    const completed = resolveTabCompletedPrompt(
      value,
      cappedSuggestion,
      maxLength,
    );
    if (!completed) return;

    event.preventDefault();
    onChange(completed);
  };

  const actionLinkSx = {
    mr: "12px",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 400,
    color: primaryColor,
    cursor: "pointer",
    border: "none",
    background: "none",
    p: 0,
    fontFamily: "inherit",
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
    "&:hover": { textDecoration: "underline" },
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 0.75,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Typography sx={{ ...formFieldLabelSx, mb: 0, ...labelSx }}>
            Assessment Prompt
            {required ? (
              <Box component="span" sx={{ color: "#E30000", ml: 0.25 }}>
                *
              </Box>
            ) : null}
          </Typography>
          {showDefaultPromptInfo ? (
            <Tooltip
              title={ASSESSMENT_PROMPT_DEFAULT_INFO}
              arrow
              placement="top"
              enterTouchDelay={0}
            >
              <IconButton
                size="small"
                aria-label="Assessment prompt default hint"
                sx={{
                  ml: 0.25,
                  p: 0.25,
                  color: "#9CA3AF",
                  "&:hover": { color: "#6B7280", bgcolor: "transparent" },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {showUseSuggested ? (
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={applySuggestion}
              sx={actionLinkSx}
            >
              Use Suggested
            </Link>
          ) : null}
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#6b7280",
              fontWeight: 400,
              ...counterSx,
            }}
          >
            {value.length.toLocaleString()}/{maxLength.toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <FormControl fullWidth error={error}>
        <Box sx={{ position: "relative", width: "100%" }}>
          {ghostSuffix ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                px: "14px",
                py: "16.5px",
                boxSizing: "border-box",
                pointerEvents: "none",
                overflow: "hidden",
                ...sharedTextAreaTypographySx,
                zIndex: 0,
              }}
            >
              <Box component="span" sx={{ color: "transparent" }}>
                {value}
              </Box>
              <Box component="span" sx={{ color: GHOST_PLACEHOLDER_COLOR }}>
                {ghostSuffix}
              </Box>
            </Box>
          ) : null}
          <OutlinedInput
            fullWidth
            multiline
            minRows={4}
            value={value}
            disabled={disabled}
            placeholder={
              showUseSuggested && value === "" ? cappedSuggestion : undefined
            }
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (nextValue.length > maxLength) return;
              onChange(nextValue);
            }}
            onKeyDown={handleKeyDown}
            sx={{
              ...outlinedInputSx,
              ...inputSx,
              position: "relative",
              zIndex: 1,
              bgcolor: "transparent",
            }}
          />
        </Box>
        {showTabHint ? (
          <Typography
            sx={{
              mt: "8px",
              fontSize: 12,
              lineHeight: "16px",
              fontStyle: "italic",
              color: TAB_HINT_COLOR,
            }}
          >
            Press{" "}
            <Box
              component="span"
              sx={{ color: primaryColor, fontStyle: "italic" }}
            >
              Tab
            </Box>{" "}
            to accept suggestion
          </Typography>
        ) : null}
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    </Box>
  );
}
